import { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { Truck, MapPinned, FileDown, Package, Info, ChevronRight, AlertTriangle } from 'lucide-react';

const emptyAddress = { name: '', phone: '', street: '', city: '', state: '', country: 'MX', postalCode: '' };
const emptyPackage = { content: 'Producto', declaredValue: 500, weight: 1, dimensions: { length: 20, width: 15, height: 10 } };

/**
 * OrderShipmentPanel - shown inside order detail (both roles).
 *   - Seller: can generate a shipping label (envia.com) if not created yet.
 *   - Buyer: sees tracking info + label link + button to track.
 */
export const OrderShipmentPanel = ({ order, currentUserId, onUpdated }) => {
    const [ship, setShip] = useState(null);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [rates, setRates] = useState([]);
    const [busy, setBusy] = useState(false);
    const [step, setStep] = useState(1); // 1: addresses/pkg, 2: rates
    const [origin, setOrigin] = useState({ ...emptyAddress });
    const [destination, setDestination] = useState({ ...emptyAddress });
    const [pkg, setPkg] = useState({ ...emptyPackage, declaredValue: order?.item_price || 500 });
    const [trackData, setTrackData] = useState(null);

    const isSeller = order?.seller_id === currentUserId;
    const isBuyer = order?.buyer_id === currentUserId;

    const load = useCallback(async () => {
        if (!order?.id) return;
        setLoading(true);
        try {
            const { data } = await api.get(`/orders/${order.id}/shipment`);
            setShip(data.shipment);
        } catch { setShip(null); } finally { setLoading(false); }
    }, [order?.id]);

    useEffect(() => { load(); }, [load]);

    // Autofill origin (seller default) + destination (order.shipping_address or buyer default) when dialog opens
    useEffect(() => {
        if (!open) return;
        const asAddr = (a) => a ? {
            name: a.name || '', phone: a.phone || '', street: a.street || '',
            city: a.city || '', state: a.state || '',
            country: a.country || 'MX', postalCode: a.postalCode || a.postal_code || '',
        } : { ...emptyAddress };
        // Order shipping_address (buyer's saved at checkout time)
        if (order?.shipping_address) {
            setDestination(asAddr(order.shipping_address));
        }
        // Seller default pickup address
        api.get('/me/addresses').then(({ data }) => {
            if (data?.default_pickup_address) setOrigin(asAddr(data.default_pickup_address));
        }).catch(() => {});
    }, [open, order?.shipping_address]);

    const quoteRates = async () => {
        if (!origin.postalCode || !destination.postalCode) { toast.error('Completa códigos postales'); return; }
        setBusy(true);
        try {
            const { data } = await api.post(`/me/orders/${order.id}/shipment/quote`, { origin, destination, packages: [pkg] });
            const list = data.rates || [];
            if (list.length === 0) { toast.info('No hay tarifas disponibles con estos datos.'); }
            setRates(list.sort((a, b) => (a.totalPrice || 0) - (b.totalPrice || 0)));
            setStep(2);
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error al cotizar'); } finally { setBusy(false); }
    };

    const generate = async (rate) => {
        setBusy(true);
        try {
            const { data } = await api.post(`/me/orders/${order.id}/shipment/generate`, {
                origin, destination, packages: [pkg], carrier: rate.carrier, service: rate.service,
            });
            toast.success(`Guía generada · ${data.trackingNumber}`);
            setOpen(false);
            setStep(1);
            setRates([]);
            setShip(data);
            onUpdated && onUpdated();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error al generar guía'); } finally { setBusy(false); }
    };

    const track = async () => {
        if (!ship?.trackingNumber) return;
        try {
            const { data } = await api.get(`/envios/track/${encodeURIComponent(ship.trackingNumber)}`);
            setTrackData(data);
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error al rastrear'); }
    };

    if (loading) return null;

    // Existing shipment (both roles see it)
    if (ship?.trackingNumber) {
        return (
            <Card data-testid="order-shipment-panel">
                <CardContent className="p-4 space-y-3">
                    <div className="flex items-center gap-2 flex-wrap">
                        <Truck className="h-5 w-5 text-[hsl(var(--primary))]" />
                        <h3 className="font-semibold text-sm">Envío OkEnvíos</h3>
                        <Badge className="bg-emerald-100 text-emerald-800 uppercase">{ship.carrier}</Badge>
                        {ship.status === 'cancelled' && <Badge variant="outline" className="border-destructive text-destructive">Cancelada</Badge>}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                        <div><span className="text-muted-foreground">Rastreo:</span> <span className="font-mono font-semibold" data-testid="tracking-number">{ship.trackingNumber}</span></div>
                        {ship.service && <div><span className="text-muted-foreground">Servicio:</span> {ship.service}</div>}
                        {ship.totalPrice && <div><span className="text-muted-foreground">Costo:</span> ${ship.totalPrice} {ship.currency}</div>}
                        <div><span className="text-muted-foreground">CP:</span> {ship.origin_zip} → {ship.destination_zip}</div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        {ship.labelUrl && <Button size="sm" variant="outline" asChild><a href={ship.labelUrl} target="_blank" rel="noreferrer"><FileDown className="h-3.5 w-3.5 mr-1" />Descargar guía</a></Button>}
                        {ship.trackUrl && <Button size="sm" variant="outline" asChild><a href={ship.trackUrl} target="_blank" rel="noreferrer">Rastrear con {ship.carrier}</a></Button>}
                        <Button size="sm" onClick={track} data-testid="order-track-btn"><MapPinned className="h-3.5 w-3.5 mr-1" />Estado en vivo</Button>
                    </div>
                    {trackData && (
                        <div className="mt-2 text-xs bg-slate-50 border rounded p-3">
                            <div className="flex items-center gap-1 mb-1"><Info className="h-3.5 w-3.5" /><span className="font-semibold">Último estado</span></div>
                            <pre className="whitespace-pre-wrap max-h-40 overflow-auto text-[10px]">{JSON.stringify(trackData, null, 2)}</pre>
                        </div>
                    )}
                </CardContent>
            </Card>
        );
    }

    // No shipment yet: seller can create; buyer sees pending
    if (isBuyer) {
        return (
            <Alert className="border-slate-200 bg-slate-50" data-testid="order-shipment-buyer-pending">
                <Package className="h-4 w-4" />
                <AlertDescription className="text-xs">
                    El vendedor aún no ha generado la guía de envío. Recibirás una notificación con el número de rastreo cuando se cree.
                </AlertDescription>
            </Alert>
        );
    }

    if (!isSeller) return null;

    return (
        <Card data-testid="order-shipment-empty">
            <CardContent className="p-4 space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                    <Truck className="h-5 w-5 text-[hsl(var(--primary))]" />
                    <h3 className="font-semibold text-sm">Generar guía OkEnvíos</h3>
                </div>
                <p className="text-xs text-muted-foreground">Crea una guía de envío para este pedido. El comprador verá el rastreo automáticamente.</p>
                <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setStep(1); setRates([]); } }}>
                    <DialogTrigger asChild>
                        <Button size="sm" data-testid="generate-label-btn"><Truck className="h-4 w-4 mr-1" />Cotizar y crear guía</Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-3xl">
                        <DialogHeader><DialogTitle>Crear guía · Pedido {order?.id?.slice(0, 8)}…</DialogTitle></DialogHeader>
                        {step === 1 && (
                            <div className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <div className="space-y-2">
                                        <div className="text-xs font-semibold uppercase text-muted-foreground">Origen (tú)</div>
                                        <div className="grid grid-cols-2 gap-2">
                                            <Input placeholder="Nombre" value={origin.name} onChange={(e) => setOrigin({ ...origin, name: e.target.value })} />
                                            <Input placeholder="Teléfono" value={origin.phone} onChange={(e) => setOrigin({ ...origin, phone: e.target.value })} />
                                            <Input className="col-span-2" placeholder="Calle y número" value={origin.street} onChange={(e) => setOrigin({ ...origin, street: e.target.value })} />
                                            <Input placeholder="Ciudad" value={origin.city} onChange={(e) => setOrigin({ ...origin, city: e.target.value })} />
                                            <Input placeholder="Estado (NL)" value={origin.state} onChange={(e) => setOrigin({ ...origin, state: e.target.value })} />
                                            <Input placeholder="CP" value={origin.postalCode} onChange={(e) => setOrigin({ ...origin, postalCode: e.target.value })} data-testid="ship-origin-zip" />
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <div className="text-xs font-semibold uppercase text-muted-foreground">Destino (comprador)</div>
                                        <div className="grid grid-cols-2 gap-2">
                                            <Input placeholder="Nombre" value={destination.name} onChange={(e) => setDestination({ ...destination, name: e.target.value })} />
                                            <Input placeholder="Teléfono" value={destination.phone} onChange={(e) => setDestination({ ...destination, phone: e.target.value })} />
                                            <Input className="col-span-2" placeholder="Calle y número" value={destination.street} onChange={(e) => setDestination({ ...destination, street: e.target.value })} />
                                            <Input placeholder="Ciudad" value={destination.city} onChange={(e) => setDestination({ ...destination, city: e.target.value })} />
                                            <Input placeholder="Estado" value={destination.state} onChange={(e) => setDestination({ ...destination, state: e.target.value })} />
                                            <Input placeholder="CP" value={destination.postalCode} onChange={(e) => setDestination({ ...destination, postalCode: e.target.value })} data-testid="ship-dest-zip" />
                                        </div>
                                    </div>
                                </div>
                                <div className="border-t pt-3">
                                    <div className="text-xs font-semibold uppercase text-muted-foreground mb-2">Paquete</div>
                                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                                        <Input className="col-span-2" placeholder="Contenido" value={pkg.content} onChange={(e) => setPkg({ ...pkg, content: e.target.value })} />
                                        <Input type="number" placeholder="Valor" value={pkg.declaredValue} onChange={(e) => setPkg({ ...pkg, declaredValue: parseFloat(e.target.value) || 0 })} />
                                        <Input type="number" step="0.1" placeholder="Peso kg" value={pkg.weight} onChange={(e) => setPkg({ ...pkg, weight: parseFloat(e.target.value) || 0 })} />
                                        <div className="grid grid-cols-3 gap-1">
                                            <Input type="number" placeholder="Alto" value={pkg.dimensions.height} onChange={(e) => setPkg({ ...pkg, dimensions: { ...pkg.dimensions, height: parseFloat(e.target.value) || 0 } })} />
                                            <Input type="number" placeholder="Ancho" value={pkg.dimensions.width} onChange={(e) => setPkg({ ...pkg, dimensions: { ...pkg.dimensions, width: parseFloat(e.target.value) || 0 } })} />
                                            <Input type="number" placeholder="Largo" value={pkg.dimensions.length} onChange={(e) => setPkg({ ...pkg, dimensions: { ...pkg.dimensions, length: parseFloat(e.target.value) || 0 } })} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        {step === 2 && (
                            <div className="space-y-2 max-h-96 overflow-auto">
                                {rates.length === 0 && <div className="text-sm text-muted-foreground text-center py-6">Sin tarifas disponibles.</div>}
                                {rates.map((r, idx) => (
                                    <div key={idx} className="flex items-center justify-between border rounded p-3">
                                        <div>
                                            <div className="font-semibold text-sm uppercase">{r.carrier} · {r.serviceDescription || r.service}</div>
                                            <div className="text-xs text-muted-foreground">{r.deliveryEstimate || 'Entrega estándar'}</div>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-lg font-bold">${r.totalPrice} <span className="text-[10px] text-muted-foreground">{r.currency || 'MXN'}</span></div>
                                            <Button size="sm" onClick={() => generate(r)} disabled={busy} data-testid={`rate-generate-${idx}`}>Elegir <ChevronRight className="h-3 w-3 ml-1" /></Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        <DialogFooter>
                            {step === 2 && <Button variant="ghost" onClick={() => setStep(1)}>← Volver</Button>}
                            {step === 1 && <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>}
                            {step === 1 && <Button onClick={quoteRates} disabled={busy} data-testid="rate-quote-btn">{busy ? 'Cotizando…' : 'Ver tarifas'}</Button>}
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </CardContent>
        </Card>
    );
};

export default OrderShipmentPanel;
